import { createSlice as createLiveHelperModalSlice } from '@liveHelper/models/modal/modalRegistry'
import { merge } from 'es-toolkit'
import { create } from 'zustand'

import { isDebug } from '@core/debug'
import type { GetState, NamedSet, UseBoundStore } from '@core/zustand'
import { createContext } from '@core/zustand'
import { withLenses } from '@core/zustand/lens'

import type { LiveHelperState, StateCreator } from './actions'
import { actions, initialState } from './actions'
import { addDevtool } from './helpers'
import { addLocalStoragePersist } from './persistInLocalStorage'

/**
 * State creator that merges initial state with custom state.
 */
export function createInitStateCreator(
  extraState?: (
    set?: NamedSet<LiveHelperState>,
    get?: GetState<LiveHelperState>,
  ) => Partial<LiveHelperState>,
) {
  return (
    set: NamedSet<LiveHelperState>,
    get: GetState<LiveHelperState>,
  ): LiveHelperState => {
    let result = merge({}, initialState)
    result = merge(result, { actions: { ...actions(set, get) } })
    result = merge(result, extraState?.(set, get) || {})
    result = merge(result, createLiveHelperModalSlice() as any)
    return result as LiveHelperState
  }
}

/**
 * @param extraState Custom state to be merged with initial state.
 * @returns A zustand store
 */
export const createStore = (
  name: string,
  extraState?: (
    set?: NamedSet<LiveHelperState>,
    get?: GetState<LiveHelperState>,
  ) => Partial<LiveHelperState>,
): UseBoundStore<LiveHelperState> => {
  let createState: StateCreator = createInitStateCreator(extraState)
  createState = withLenses<LiveHelperState>(createState) as StateCreator

  // Note: devtool must be configured before other middleware.
  if (isDebug()) {
    createState = addDevtool(createState, name)
  }

  return create<LiveHelperState>(addLocalStoragePersist(createState))
}

export const { Provider, useStore, useStoreApi } =
  createContext<LiveHelperState>()
